import React, { FC, useEffect, useState, useCallback, useMemo } from 'react';
import { Form, Input, Radio, Modal, message, Button } from 'antd';
import { Icon as LegacyIcon } from '@ant-design/compatible';
import { imageUploadValidation } from '../../utils/ImageUtils';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { getProtectedImageUriForBuyer } from '../../utils/FileAccessUtils';
import OWUpload from '../OWUpload';
import {
	CHOICE_FOLLOW_UP_ACTION,
	findChoiceForAnswer,
	SITE_SURVEY_ANSWER_TYPE,
} from '../../utils/SiteSurveyUtils';
import { connect } from 'react-redux';
import WalkthroughTaskSection from '../ow_inline_todo_list/WalkthroughTaskSection';
import WalkthroughTaskListAnswerDisplay from '../walkthroughs_detail_details_page/WalkthroughTaskListAnswerDisplay';
import WorkOrderTaskButton from '../work_order_task_button/WorkOrderTaskButton';
import WorkOrderCreateButton from './WorkOrderCreateButton';
import { getLinkWIthBackLinkParams } from '../../utils/HistoryUtils';

const WORK_ORDER_BACK_TEXT = 'Back to Walkthrough';

const FORM_ID = 'walkthrough-answer-edit-form';
interface TaskEditFormProps {
	taskAnswer: any;
	onCancel: () => void;
	onSave: (answer: any) => any;
	walkthrough: any;
	currentUser: any;
}

interface FieldData {
	name: string;
	value?: any;
}

const TaskEditPopup: FC<TaskEditFormProps> = ({
	taskAnswer,
	onCancel,
	onSave,
	walkthrough,
}): React.ReactElement => {
	const [form] = Form.useForm();

	const [fields, setFields] = useState<FieldData[]>([]);

	const isTaskListAnswer = useMemo(
		() => taskAnswer.questionType === SITE_SURVEY_ANSWER_TYPE.TASK_LIST,
		[taskAnswer.questionType]
	);

	const isChecklistAnswer = useMemo(
		() => taskAnswer.questionType === SITE_SURVEY_ANSWER_TYPE.CHECKLIST,
		[taskAnswer.questionType]
	);

	const skipAnswerSubmission = isTaskListAnswer || isChecklistAnswer;

	const buildAttachmentList = (attachmentData) =>
		(attachmentData || []).map((p, idx) => {
			const imgFileName = nullSafeGetOrElse('fileName', p, '');
			const imgFileId = nullSafeGetOrElse('fileId', p, '');
			const thumbUri = getProtectedImageUriForBuyer(imgFileId, imgFileName, 100, 100);
			const fullUri = getProtectedImageUriForBuyer(imgFileId, imgFileName, 400, 400);
			return {
				uid: idx,
				size: 1,
				name: imgFileName,
				filename: imgFileName,
				url: fullUri,
				status: 'done',
				thumbUrl: thumbUri,
				response: { data: { fileName: imgFileName, fileId: imgFileId } },
				type: 'image/jpeg',
			};
		});

	const initialList = useMemo(() => buildAttachmentList(taskAnswer.attachments), [taskAnswer]);
	const [attachments, setAttachments] = useState(initialList);
	const [uploading, setUploading] = useState(false);
	const [saving, setSaving] = useState(false);
	// Gates the inline requirement error messages. The red asterisk on a field
	// label appears as soon as the selected answer makes it required, but the
	// "Comment required…" style errors stay hidden until the user actually
	// attempts to save — so picking an answer doesn't immediately scold them.
	const [showRequirementErrors, setShowRequirementErrors] = useState(false);
	const [previewVisible, setPreviewVisible] = useState(false);
	const [previewImage, setPreviewImage] = useState('');

	const watchedAnswer = Form.useWatch('answer', form);
	const watchedComments = Form.useWatch('comments', form);

	const isMultipleChoice = taskAnswer.questionType === SITE_SURVEY_ANSWER_TYPE.MULTIPLE_CHOICE;

	const selectedChoice = useMemo(
		() =>
			isMultipleChoice
				? findChoiceForAnswer(watchedAnswer, taskAnswer.multipleChoicesWithLabel)
				: null,
		[isMultipleChoice, watchedAnswer, taskAnswer]
	);

	// Follow-up actions can be required at the task level (any answer type) and/or
	// on the selected multiple-choice option. A requirement is enforced if either
	// source asks for it.
	const taskFollowUpActions = nullSafeGetOrElse('followUpActions', taskAnswer, []);
	const choiceFollowUpActions = nullSafeGetOrElse('followUpActions', selectedChoice, []);
	const followUpActions = [...taskFollowUpActions, ...choiceFollowUpActions];
	const requiresComment = followUpActions.includes(CHOICE_FOLLOW_UP_ACTION.REQUIRE_COMMENT);
	const requiresPhoto = followUpActions.includes(CHOICE_FOLLOW_UP_ACTION.REQUIRE_PHOTO);
	const requiresWorkOrder = followUpActions.includes(CHOICE_FOLLOW_UP_ACTION.REQUIRE_WORK_ORDER);

	// workOrders is the populated array on the answer record today; workOrderIds is the
	// raw shape the backend exposes — fall back so this keeps working if/when the
	// payload moves to IDs.
	const linkedWorkOrderCount = nullSafeGetOrElse(
		'workOrders.length',
		taskAnswer,
		nullSafeGetOrElse('workOrderIds.length', taskAnswer, 0)
	);

	const missingComment = requiresComment && (!watchedComments || !watchedComments.trim());
	const missingPhoto = requiresPhoto && attachments.length === 0;
	const missingWorkOrder = requiresWorkOrder && linkedWorkOrderCount === 0;
	const hasUnmetRequirements = missingComment || missingPhoto || missingWorkOrder;

	const selectedChoiceLabel = nullSafeGet('value', selectedChoice);

	// A requirement triggered by the selected choice gets the choice-specific copy;
	// a task-level requirement (or non-multiple-choice answer) gets generic copy.
	const requirementReason = (action) =>
		choiceFollowUpActions.includes(action) && selectedChoiceLabel
			? ` because you selected "${selectedChoiceLabel}"`
			: ' for this task';

	const getWorkOrderCreateLink = useCallback(
		(currentLocation, BACK_TEXT, path) => {
			const taskAnswers = nullSafeGetOrElse('taskAnswers', walkthrough, []);
			const taskIndex = taskAnswers.findIndex(
				(taskItem) => taskItem.titleId === taskAnswer.titleId
			);
			const params = {
				locationId: nullSafeGet('locationId', walkthrough),
				images: (taskAnswer.attachments || []).map(
					({ fileId, fileName }) => `${fileId}/${fileName}`
				),
				walkthroughId: nullSafeGet('id', walkthrough),
				siteSurveyTaskTitleId: taskAnswer.titleId,
				walkthroughTaskIndex: taskIndex,
			};
			const paramsString = new URLSearchParams(params).toString();
			return getLinkWIthBackLinkParams(currentLocation, BACK_TEXT, `${path}?${paramsString}`);
		},
		[walkthrough, taskAnswer]
	);

	useEffect(() => {
		setFields([
			{
				name: 'answer',
				value: taskAnswer.answer,
			},
			{
				name: 'comments',
				value: taskAnswer.comments,
			},
		]);
	}, [form, taskAnswer]);

	const getFormField = useCallback(
		(task) => {
			switch (task.questionType) {
				case SITE_SURVEY_ANSWER_TYPE.MULTIPLE_CHOICE:
					return nullSafeGetOrElse('multipleChoicesWithLabel.length', task, 0) !== 0 ? (
						<Radio.Group>
							{task.multipleChoicesWithLabel.map((choice, idx) => {
								const value = `${choice.value}${
									choice.description ? ` (${choice.description})` : ''
								}`;
								return (
									<Radio value={value} key={`rg-${idx}`}>
										{value}
									</Radio>
								);
							})}
						</Radio.Group>
					) : (
						''
					);
				case SITE_SURVEY_ANSWER_TYPE.TASK_LIST:
					return (
						<WalkthroughTaskSection
							notCheckable={false}
							existingTasks={nullSafeGetOrElse('taskListAnswer', task, [])}
							timeZoneId={nullSafeGet('location.timeZoneId', walkthrough)}
							associationDisabled
							addGroupDisabled
							isAllowedToEdit
							retainTaskObject
							walkthrough={walkthrough}
							siteSurveyTaskTitleId={nullSafeGetOrElse('titleId', task, '')}
							hideIndividualWalkthroughLink
						/>
					);
				case SITE_SURVEY_ANSWER_TYPE.CHECKLIST:
					return (
						<WalkthroughTaskListAnswerDisplay
							taskAnswer={task}
							timeZoneId={nullSafeGet('location.timeZoneId', walkthrough)}
							checkable={nullSafeGet('status', walkthrough) !== 'complete'}
						/>
					);
				default:
					return <Input.TextArea placeholder="" />;
			}
		},
		[walkthrough]
	);

	const handleImageUpdate = useCallback((info) => {
		setUploading(info.file.status === 'uploading');
		if (info.file.status !== undefined) {
			setAttachments(info.fileList);
		}
	}, []);

	const handleSubmit = useCallback(
		(values) => {
			if (hasUnmetRequirements) {
				setShowRequirementErrors(true);
				message.error('Please complete the required fields highlighted above.');
				return;
			}
			setSaving(true);

			onSave({
				...taskAnswer,
				answer: skipAnswerSubmission ? undefined : values.answer,
				comments: values.comments,
				attachments: attachments.map((attachment) => attachment.response.data),
			})
				.catch(message.error)
				.finally(() => setSaving(false));
		},
		[onSave, taskAnswer, skipAnswerSubmission, attachments, hasUnmetRequirements]
	);

	const handleCancel = useCallback(() => {
		setPreviewVisible(false);
		setPreviewImage('');
	}, []);

	const handleImagePreview = useCallback((obj) => {
		if (!obj.response.data) return;

		const imgFileId = obj.response.data.fileId;
		const imgFileName = obj.response.data.fileName;
		const thumbUrl = getProtectedImageUriForBuyer(imgFileId, imgFileName, 100, 100);
		const fullUrl = getProtectedImageUriForBuyer(imgFileId, imgFileName, 400, 400);
		setPreviewImage(fullUrl || thumbUrl);
		setPreviewVisible(true);
	}, []);
	const isImageUploading = (attachments || []).some((_) => _.status === 'uploading');

	// Changing the selected answer changes which fields are required, so any
	// previously surfaced errors no longer apply — hide them again until the
	// user attempts to save under the new selection.
	const handleValuesChange = useCallback((changedValues) => {
		if ('answer' in changedValues) {
			setShowRequirementErrors(false);
		}
	}, []);

	return (
		<Modal
			visible={true}
			title="Edit Answer"
			onCancel={onCancel}
			footer={[
				<Button onClick={onCancel} size="large">
					Cancel
				</Button>,
				<Button
					type="primary"
					size="large"
					style={{ marginLeft: '16px' }}
					key="submit"
					htmlType="submit"
					form={FORM_ID}
					loading={saving || isImageUploading}
				>
					Save
				</Button>,
			]}
			closable
		>
			<Form
				id={FORM_ID}
				form={form}
				layout="vertical"
				fields={fields}
				onFinish={handleSubmit}
				onValuesChange={handleValuesChange}
			>
				<Form.Item
					name="answer"
					label={taskAnswer.title}
					initialValue={
						isTaskListAnswer
							? taskAnswer.taskListAnswer
							: isChecklistAnswer
							? taskAnswer.checklistAnswer
							: taskAnswer.answer
					}
				>
					{getFormField(taskAnswer)}
				</Form.Item>
				<Form.Item
					name="comments"
					label="Comments"
					required={requiresComment}
					validateStatus={showRequirementErrors && missingComment ? 'error' : undefined}
					help={
						showRequirementErrors && missingComment
							? `Comment required${requirementReason(CHOICE_FOLLOW_UP_ACTION.REQUIRE_COMMENT)}`
							: undefined
					}
				>
					<Input.TextArea />
				</Form.Item>
				<Form.Item
					name="attachments"
					label="Images"
					required={requiresPhoto}
					validateStatus={showRequirementErrors && missingPhoto ? 'error' : undefined}
					help={
						showRequirementErrors && missingPhoto
							? `Photo required${requirementReason(CHOICE_FOLLOW_UP_ACTION.REQUIRE_PHOTO)}`
							: undefined
					}
				>
					<OWUpload
						name="file"
						fileList={attachments}
						listType="picture-card"
						accept="image/*"
						multiple={true}
						onPreview={handleImagePreview}
						beforeUpload={imageUploadValidation}
						onChange={handleImageUpdate}
					>
						<div style={{ display: 'inline' }}>
							<LegacyIcon type={uploading ? 'loading' : 'plus'} />
							<div className="ant-upload-text">Add Image</div>
						</div>
					</OWUpload>
					<Modal visible={previewVisible} footer={null} onCancel={handleCancel}>
						<img alt="example" style={{ width: '100%' }} src={previewImage} />
					</Modal>
				</Form.Item>
				{requiresWorkOrder ? (
					<Form.Item
						label="Work Orders"
						required={requiresWorkOrder}
						validateStatus={showRequirementErrors && missingWorkOrder ? 'error' : undefined}
						help={
							showRequirementErrors && missingWorkOrder
								? `Link or create a work order${requirementReason(
										CHOICE_FOLLOW_UP_ACTION.REQUIRE_WORK_ORDER
								  )}`
								: undefined
						}
					>
						<div className="flex flex-row items-center gap-2">
							<WorkOrderTaskButton
								task={taskAnswer}
								locationId={nullSafeGet('locationId', walkthrough)}
							/>
							<WorkOrderCreateButton
								size="middle"
								backText={WORK_ORDER_BACK_TEXT}
								title="Create Work Order"
								getLink={getWorkOrderCreateLink}
							/>
						</div>
						<div className="mt-2 text-character-secondary">
							{linkedWorkOrderCount > 0
								? `${linkedWorkOrderCount} work order${
										linkedWorkOrderCount > 1 ? 's' : ''
								  } linked to this task`
								: 'No work orders linked yet'}
						</div>
					</Form.Item>
				) : null}
			</Form>
		</Modal>
	);
};

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
});

export default connect(mapStateToProps)(TaskEditPopup as any);
